import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { EMPLOYMENT_FAQ_CATEGORIES } from "@/content/employment-corpus/faq-defs";

export const Route = createFileRoute("/en/employment/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/employment-corpus");
    return { faqs: m.EMPLOYMENT_FAQ_EN };
  },
  component: EmploymentFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Documents for Overseas Jobs & Scholarships — ${faqs.length} FAQs`,
        description:
          "Certifying Thai documents for overseas employment and scholarships: employment letters, degrees and transcripts, professional licences and Certificates of Good Standing, VETASSESS/WES/ENIC-NARIC assessment, police clearance, NAATI and sworn translation, MFA legalisation and the Apostille from 28 February 2027.",
        path: "/en/employment/faq",
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/employment/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Overseas Jobs & Scholarships FAQ", url: `${site}/en/employment/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/employment/faq")) },
      ],
    };
  },
});

function EmploymentFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Overseas Jobs &amp; Scholarships FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Certifying documents for overseas jobs and scholarships — {faqs.length} FAQs
        </h1>
        <p data-speakable="true" className="speakable-summary mb-6 text-muted-foreground">
          Answers follow the practice of the Legalisation Division of the Thai Department of Consular Affairs
          (consular.mfa.go.th), the Department of Employment (doe.go.th), the Office of the Civil Service Commission
          (ocsc.go.th), MHESI (mhesi.go.th), the Criminal Records Division of the Royal Thai Police, Thai professional
          councils, and overseas assessors including NAATI, VETASSESS, Engineers Australia, ACS, WES and ENIC-NARIC. The
          Apostille Convention enters into force for Thailand on 28 February 2027. General information, not an official
          determination.
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/employment/faq" className="text-primary underline">รับรองเอกสารสมัครงาน/สมัครทุนต่างประเทศ →</a>
        </div>
        <section aria-label="Categories" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {EMPLOYMENT_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/employment/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Talk to our team</h2>
          <p className="text-sm text-muted-foreground">
            Tell us the destination country, the receiving body and your deadline by phone, LINE or email, Mon-Sat
            9:00-18:00, and we will sequence the certification steps around document validity.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact us</a>
        </section>
        <FaqSourceNote topic="employment" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}